import React, { useMemo, useCallback, useState, useEffect, Fragment } from 'react';
import { ClassType } from '../../api';
import { LineupEntry, Period, Reference, SessionEvent, secondsToTimeSpan, timeSpanToSeconds } from '../../services/sessionModels';
import { EventPlayerType, EventReason } from '../../services/sessionsPrepare';
import { makeStyles, Select, MenuItem, IconButton, Tooltip, Typography, Checkbox } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { EventReasonToString } from './SessionEvents';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import TimeInputField from '../Util/TimeInputField';
import ArrowDropDownCircleSharpIcon from '@material-ui/icons/ArrowDropDownCircleSharp';

const useStyles = makeStyles((theme) => ({
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    height: '5%',
  },
  list: {
    overflowY: 'scroll',
    display: 'flex',
    height: '92%',
    flexDirection: 'column',
    padding: '4px',
    marginTop: '12px',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#eee',
    marginBottom: '8px',
    padding: '8px',
    cursor: 'pointer',
  },
  selectedItem: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#eee',
    border: '2px solid ' + theme.palette.primary.main,
    marginBottom: '8px',
    padding: '8px',
  },
  itemRow: {
    padding: '0px',
    display: 'flex',
    flexDirection: 'row',
    fontSize: '10px',
    height: '30px',
    width: '100%',
  },
  leftColumn: {
    width: '110px',
    textAlign: 'left',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  middleColumn: {
    'flex-grow': 0,
    width: '240px',
    fontSize: '12px',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  rightColumn: {
    width: '30px',
    textAlign: 'center',
  },
  columnTitle: {
    fontSize: 12,
  },
  deleteIcon: {
    display: 'inline',
    marginLeft: 'auto',
    marginRight: 0,
    color: '#000',
  },
  textField: {
    paddingTop: 0,
  },
  textFieldInput: {
    fontSize: '12px',
  },
  selectBox: {
    fontSize: '12px',
    width: '100%',
  },
  timeTextField: {
    fontSize: '12px',
    fontWeight: 'bold',
    '& .Mui-disabled': {
      color: '#000',
      fontWeight: 'normal',
    },
  },
  icon: {
    display: 'inline',
    color: '#000',
  },
  setTimeIcon: {
    display: 'inline',
    color: '#00a',
  },
  customCheckbox: {
    paddingLeft: '0px',
    paddingTop: '4px',
  },
}));

const classTypeOf = (event: SessionEvent, homeTeam: Reference | null | undefined) => {
  if (event.out?.type === EventPlayerType.Player) {
    if (event.teamId === homeTeam?.id) {
      return ClassType.HomePlayer;
    } else {
      return ClassType.AwayPlayer;
    }
  } else {
    if (event.teamId === homeTeam?.id) {
      return ClassType.HomeKeeper;
    } else {
      return ClassType.AwayKeeper;
    }
  }
};

const playerText = (entry: LineupEntry) => {
  return entry.shirtNumber + '. ' + (entry.player?.name || '');
};

type Props = {
  events: SessionEvent[];
  homeTeam: Reference | undefined | null;
  awayTeam: Reference | undefined | null;
  addEvent: () => void;
  editEvent: (event: SessionEvent) => void;
  deleteEvent: (event: SessionEvent) => void;
  playTime: number;
  seekToPlayTime: (time: number) => void;
  fullID: boolean;
  lineup: Array<LineupEntry>;
  periods: Array<Period>;
};

const SessionEventList = ({
  events,
  homeTeam,
  awayTeam,
  addEvent,
  editEvent,
  deleteEvent,
  playTime,
  seekToPlayTime,
  fullID,
  lineup,
  periods,
}: Props) => {
  const classes = useStyles();
  const [selectedEvent, setSelectedEvent] = useState<SessionEvent | undefined>(
    events.length > 0 ? events[0] : undefined
  );

  useEffect(() => {
    seekToPlayTime(timeSpanToSeconds(selectedEvent?.timestamp));
  }, [selectedEvent, seekToPlayTime]);

  const updateReason = useCallback(
    (event: SessionEvent, value: string) => {
      event.reason = value;
      editEvent(event);
    },
    [editEvent]
  );

  const updateTimestamp = useCallback(
    (event: SessionEvent, value: number) => {
      event.timestamp = secondsToTimeSpan(value);
      editEvent(event);
    },
    [editEvent]
  );

  const updateOffscreen = useCallback(
    (event: SessionEvent, value: boolean, lineupEntry?: LineupEntry) => {
      event.offscreen = value;
      if (value && lineupEntry) {
        const period = periods.find((period) => period.type === lineupEntry.periodIn);
        if (period?.startTime) {
          event.timestamp = period.startTime;
        }
      }
      editEvent(event);
    },
    [editEvent, periods]
  );

  const updateOutPlayerType = useCallback(
    (event: SessionEvent, value: ClassType) => {
      if (!event.out) return;
      const newEvent = { ...event, out: { ...event.out } };
      newEvent.out.type =
        value === ClassType.AwayPlayer || value === ClassType.HomePlayer
          ? EventPlayerType.Player
          : EventPlayerType.Keeper;
      newEvent.teamId = (value === ClassType.HomePlayer || value === ClassType.HomeKeeper ? homeTeam : awayTeam)?.id;
      editEvent(newEvent);
    },
    [awayTeam, editEvent, homeTeam]
  );

  const eventItems = useMemo(() => {
    return events.map((event: SessionEvent) => {
      const value = { ...event };

      const lineupEntryIn = lineup.find((le) => le.inEventId === value.id);
      const lineupEntryOut = lineup.find((le) => le.outEventId === value.id);
      return (
        <div
          className={event.id === selectedEvent?.id ? classes.selectedItem : classes.item}
          key={value.id}
          onClick={() => setSelectedEvent(value)}
        >
          <div className={classes.itemRow}>
            <div className={classes.leftColumn}>
              <Typography className={classes.columnTitle}>Reason</Typography>
            </div>
            <div className={classes.middleColumn}>
              <Select
                readOnly={fullID}
                className={classes.selectBox}
                value={value.reason || EventReason.RedCard}
                onChange={(e) => updateReason(value, e.target.value as string)}
              >
                <MenuItem value={EventReason.Substitution}>
                  {EventReasonToString(EventReason.Substitution)}
                </MenuItem>
                <MenuItem value={EventReason.RedCard}>
                  {EventReasonToString(EventReason.RedCard)}
                </MenuItem>
                <MenuItem value={EventReason.Injury}>{EventReasonToString(EventReason.Injury)}</MenuItem>
              </Select>
            </div>
            {!fullID && (
              <div className={classes.rightColumn}>
                <Tooltip title="Delete Event" arrow>
                  <IconButton className={classes.deleteIcon} size="small" onClick={() => deleteEvent(value)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>

          <div className={classes.itemRow}>
            <div className={classes.leftColumn}>
              <Typography className={classes.columnTitle}>Going out</Typography>
            </div>
            <div className={classes.middleColumn}>
              <Select
                readOnly={fullID}
                className={classes.selectBox}
                value={classTypeOf(value, homeTeam)}
                onChange={(e) => updateOutPlayerType(value, e.target.value as ClassType)}
              >
                <MenuItem value={ClassType.HomePlayer}>Player {homeTeam?.name || 'home'}</MenuItem>
                <MenuItem value={ClassType.AwayPlayer}>Player {awayTeam?.name || 'away'}</MenuItem>
                <MenuItem value={ClassType.HomeKeeper}>Goalkeeper {homeTeam?.name || 'home'}</MenuItem>
                <MenuItem value={ClassType.AwayKeeper}>Goalkeeper {awayTeam?.name || 'away'}</MenuItem>
              </Select>
            </div>
          </div>
          {lineupEntryIn && (
            <div className={classes.itemRow}>
              <div className={classes.leftColumn}>
                <Typography className={classes.columnTitle}>In {Math.ceil((lineupEntryIn.inSeconds ?? 0) / 60)}'</Typography>
              </div>
              <div className={classes.middleColumn}>{playerText(lineupEntryIn)}</div>
            </div>
          )}
          {lineupEntryOut && (
            <div className={classes.itemRow}>
              <div className={classes.leftColumn}>
                <Typography className={classes.columnTitle}>
                  Out {Math.ceil((lineupEntryOut.outSeconds ? lineupEntryOut.outSeconds : (lineupEntryIn?.inSeconds || -1))  / 60)}'
                </Typography>
              </div>
              <div className={classes.middleColumn}>{playerText(lineupEntryOut)}</div>
            </div>
          )}
          {!value.offscreen && (
            <div className={classes.itemRow}>
              <div className={classes.leftColumn}>
                <Typography className={classes.columnTitle}>Timestamp</Typography>
              </div>
              <div className={classes.middleColumn}>
                <TimeInputField
                  className={classes.timeTextField}
                  timeSeconds={timeSpanToSeconds(value.timestamp)}
                  onTimeChanged={(newTime: number) => {
                    updateTimestamp(value, newTime);
                  }}
                />
              </div>
              <div className={classes.rightColumn}>
                <Tooltip title="Set current video time as event time" arrow>
                  <IconButton
                    className={classes.setTimeIcon}
                    size="small"
                    onClick={() => {
                      updateTimestamp(value, playTime);
                    }}
                  >
                    <ArrowDropDownCircleSharpIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          )}
          {value.reason === EventReason.Substitution && (
            <Tooltip
              title="Check this if the substitution was done between periods (e.g. during halftime, before kickoff of the 2nd half) and the new player is in the lineup of this period"
              arrow
            >
              <div className={classes.itemRow}>
                <div className={classes.leftColumn}>
                  <Typography className={classes.columnTitle}>Between periods</Typography>
                </div>
                <div className={classes.middleColumn}>
                  <Checkbox
                    checked={value.offscreen || false}
                    onChange={(e, checked) => updateOffscreen(value, checked, lineupEntryIn)}
                  />
                </div>
              </div>
            </Tooltip>
          )}
        </div>
      );
    });
  }, [
    events,
    selectedEvent,
    classes,
    homeTeam,
    awayTeam,
    updateOutPlayerType,

    playTime,
    updateTimestamp,
    deleteEvent,
    updateReason,
    updateOffscreen,
    fullID,
    lineup,
  ]);

  return (
    <>
      <div className={classes.topRow}>
        <span>Events</span>
        {!fullID && (
          <span>
            <Tooltip title="Add a new event" arrow>
              <IconButton onClick={addEvent}>
                <AddCircleIcon color="primary" style={{ fontSize: 32 }} />
              </IconButton>
            </Tooltip>
          </span>
        )}
      </div>
      <div className={classes.list}>{eventItems}</div>
    </>
  );
};

export default SessionEventList;
